import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { IconEdit } from '../../../../../../../../assets/icons';
import { Checkbox } from '../../../../../../../../components/checkbox';
import { DialogTimePicker } from '../../../../../../../../components/dialog-time-picker';
import { Header } from '../../../../../../../../components/header';
import { useMeterConfigurationContext } from '../../context';
import { useMeterConfigErrorMessage } from '../../hooks/use-error-message';
import { EventingRuleBody } from '../../types';
import { InputMui } from '../../../../../../../../components/input-mui/input-mui';

type PricePartFormProps = {
  currentRule: EventingRuleBody;
};

export const PricePartForm: React.FC<PricePartFormProps> = ({ currentRule }) => {
  const { t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');
  const [fromPicker, setFromPicker] = useState<boolean>(false);
  const [toPicker, setToPicker] = useState<boolean>(false);
  const { activeRuleIndex, form } = useMeterConfigurationContext();
  const { getErrorMessage } = useMeterConfigErrorMessage();
  const isDesktop = useMediaQuery('(min-width:800px)');

  return (
    <>
      <Header title={t('meterConfiguration.priceEvent.priceConditions')} />
      <div className="grid-list-24">
        <Controller
          name={`rules.${activeRuleIndex}.startAt`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'startAt', error);

            return (
              <>
                <InputMui
                  value={field.value}
                  readOnly
                  onClickInput={() => setFromPicker(true)}
                  endIcon={<IconEdit />}
                  label={t('from')}
                  reverse
                  {...(errorMessage && { errorMessage })}
                />
                <DialogTimePicker
                  header={t('from')}
                  open={fromPicker}
                  setOpen={setFromPicker}
                  onSave={(value) => field.onChange(value)}
                  time={field.value}
                  showSeconds
                />
              </>
            );
          }}
        />
        <Controller
          name={`rules.${activeRuleIndex}.endAt`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'endAt', error);

            return (
              <>
                <InputMui
                  value={field.value}
                  readOnly
                  onClickInput={() => setToPicker(true)}
                  endIcon={<IconEdit />}
                  label={t('to')}
                  reverse
                  {...(errorMessage && { errorMessage })}
                />
                <DialogTimePicker
                  header={t('to')}
                  open={toPicker}
                  setOpen={setToPicker}
                  onSave={(value) => field.onChange(value)}
                  time={field.value}
                  showSeconds
                />
              </>
            );
          }}
        />
      </div>
      <div className="grid-list-24">
        <Controller
          name={`rules.${activeRuleIndex}.maxPriceChecked`}
          control={form.control}
          render={({ field }) => (
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={field.value}
                reverse
                onChange={field.onChange}
                {...(isDesktop && { className: 'm-b-0' })}
              >
                {t('meterConfiguration.priceEvent.priceLower')}
              </Checkbox>
            </Stack>
          )}
        />
        <Controller
          name={`rules.${activeRuleIndex}.maxPrice`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'maxPrice', error);

            return (
              <InputMui
                value={field.value}
                label={t('meterConfiguration.priceEvent.priceLower')}
                onChange={field.onChange}
                reverse
                type="number"
                step={0.1}
                disabled={!currentRule.maxPriceChecked}
                {...(errorMessage && { errorMessage })}
              />
            );
          }}
        />
      </div>
      <div className="grid-list-24">
        <Controller
          name={`rules.${activeRuleIndex}.minPriceChecked`}
          control={form.control}
          render={({ field }) => (
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={field.value}
                reverse
                onChange={field.onChange}
                {...(isDesktop && { className: 'm-b-0' })}
              >
                {t('meterConfiguration.priceEvent.priceGreater')}
              </Checkbox>
            </Stack>
          )}
        />
        <Controller
          name={`rules.${activeRuleIndex}.minPrice`}
          control={form.control}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'minPrice', error);

            return (
              <InputMui
                value={field.value}
                label={t('meterConfiguration.priceEvent.priceGreater')}
                onChange={field.onChange}
                reverse
                type="number"
                step={0.1}
                disabled={!currentRule.minPriceChecked}
                {...(errorMessage && { errorMessage })}
              />
            );
          }}
        />
      </div>
      <div className="grid-list-24">
        <Controller
          name={`rules.${activeRuleIndex}.cheapestPeriodsChecked`}
          control={form.control}
          render={({ field }) => (
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={field.value}
                reverse
                onChange={field.onChange}
                {...(isDesktop && { className: 'm-b-0' })}
              >
                {t('meterConfiguration.priceEvent.cheapestN')}
              </Checkbox>
            </Stack>
          )}
        />
        <Controller
          name={`rules.${activeRuleIndex}.cheapestPeriods`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'cheapestPeriods', error);

            return (
              <InputMui
                value={field.value}
                label={t('meterConfiguration.priceEvent.cheapestN')}
                onChange={field.onChange}
                reverse
                type="number"
                min={1}
                max={24}
                disabled={!currentRule.cheapestPeriodsChecked}
                {...(errorMessage && { errorMessage })}
              />
            );
          }}
        />
      </div>
    </>
  );
};
